<script setup lang="ts">
  import ColumnComponent from '@/components/ColumnComponent.vue'
  import WelcomeScreen from '@/components/WelcomeScreen.vue'
  import OnboardingBanner from '@/components/onboarding/OnboardingBanner.vue'
  import { useHead } from '@unhead/vue'
  import { useI18n } from 'vue-i18n'
  import { useDeckStore } from '@/store'
  import { addColumn, moveColumn } from '@/store/mutations'
  import { useOnboardingNudges } from '@/composables/useOnboardingNudges'
  import { useGuidedTour } from '@/composables/useGuidedTour'
  import { storeToRefs } from 'pinia'
  import { useRouter, useRoute } from 'vue-router'
  import {
    nextTick,
    ref,
    watch,
    useTemplateRef,
    shallowRef,
    defineAsyncComponent
  } from 'vue'
  import { useSortable } from '@vueuse/integrations/useSortable'

  const CrossDeviceModal = defineAsyncComponent(
    () => import('@/components/onboarding/CrossDeviceModal.vue')
  )

  const deckStore = useDeckStore()
  const { columns, columnsReady } = storeToRefs(deckStore)
  const { t } = useI18n({ useScope: 'global' })
  const {
    showAddColumnNudge,
    showAiDiscoveryNudge,
    showMobileInstallNudge,
    showDesktopCrossDeviceNudge,
    showMobileCrossDeviceNudge,
    dismiss,
    promptInstall
  } = useOnboardingNudges()
  const { autoStartPending, start: startTour } = useGuidedTour()

  // Déclenche le tour une fois qu'une colonne est rendue, pour que ses ancres soient dans le DOM.
  watch(
    [columns, autoStartPending],
    async ([cols, pending]) => {
      if (cols.length > 0 && pending) {
        autoStartPending.value = false
        await nextTick()
        startTour()
      }
    },
    { immediate: true }
  )

  const crossDeviceOpen = ref(false)
  const crossDeviceMode = ref<'qr' | 'copy'>('qr')

  function openCrossDevice(mode: 'qr' | 'copy') {
    crossDeviceMode.value = mode
    crossDeviceOpen.value = true
  }

  const columnsEl = useTemplateRef('columnsEl')
  const sortableColumns = shallowRef(columns.value)
  const isDragging = ref(false)

  watch(
    columns,
    val => {
      if (!isDragging.value) sortableColumns.value = [...val]
    },
    { immediate: true }
  )

  useSortable(columnsEl, sortableColumns, {
    handle: '[data-drag-handle]',
    watchElement: true,
    animation: 150,
    forceFallback: false,
    fallbackOnBody: false,
    onStart() {
      isDragging.value = true
    },
    onEnd(evt) {
      isDragging.value = false
      if (
        evt.oldIndex === undefined ||
        evt.newIndex === undefined ||
        evt.oldIndex === evt.newIndex
      )
        return
      const column = columns.value[evt.oldIndex]
      if (column) void moveColumn(column.id, evt.newIndex)
    }
  })

  const router = useRouter()
  const route = useRoute()

  useHead({
    title: import.meta.env.VITE_APP_TITLE,
    meta: [
      {
        name: 'site',
        content: import.meta.env.VITE_APP_TITLE
      },
      {
        name: 'description',
        content:
          'Deck is a reader for AFP feeds. It allows you to find and read stories, multimedia articles and photos directly in your browser.'
      }
    ]
  })
</script>

<template>
  <WelcomeScreen v-if="columnsReady && columns.length === 0" />
  <template v-else-if="columns.length > 0">
    <OnboardingBanner
      v-if="showMobileInstallNudge"
      icon="mdi--cellphone-arrow-down"
      :text="t('onboarding.nudges.mobile-install.text')"
      :cta="t('onboarding.nudges.mobile-install.cta')"
      @cta="promptInstall"
      @dismiss="dismiss('mobileInstall')"
    />
    <OnboardingBanner
      v-else-if="showAddColumnNudge"
      icon="mdi--view-column-outline"
      :text="t('onboarding.nudges.add-column.text')"
      :cta="t('onboarding.nudges.add-column.cta')"
      @cta="addColumn()"
      @dismiss="dismiss('addColumn')"
    />
    <OnboardingBanner
      v-else-if="showAiDiscoveryNudge"
      icon="mdi--robot-happy-outline"
      :text="t('onboarding.nudges.ai-discovery.text')"
      :cta="t('onboarding.nudges.ai-discovery.cta')"
      @cta="
        router.push({ name: 'ai-new', query: { 'prompt-id': 'get-started' } })
      "
      @dismiss="dismiss('aiDiscovery')"
    />
    <OnboardingBanner
      v-else-if="showDesktopCrossDeviceNudge"
      icon="mdi--cellphone-link"
      :text="t('onboarding.nudges.desktop-cross.text')"
      :cta="t('onboarding.nudges.desktop-cross.cta')"
      @cta="openCrossDevice('qr')"
      @dismiss="dismiss('desktopCross')"
    />
    <OnboardingBanner
      v-else-if="showMobileCrossDeviceNudge"
      icon="mdi--monitor-cellphone"
      :text="t('onboarding.nudges.mobile-cross.text')"
      :cta="t('onboarding.nudges.mobile-cross.cta')"
      @cta="openCrossDevice('copy')"
      @dismiss="dismiss('mobileCross')"
    />
    <CrossDeviceModal
      v-model:open="crossDeviceOpen"
      :mode="crossDeviceMode"
    />
    <div
      ref="columnsEl"
      id="columns"
      class="bg-base-200 flex grow touch-auto snap-x snap-mandatory overflow-x-auto overflow-y-hidden pt-2 select-none print:hidden"
    >
      <ColumnComponent
        v-for="column in sortableColumns"
        :key="`column-${column.id}`"
        :column-id="column.id"
        :column-type="column.type"
        class="snap-center snap-always"
      />
    </div>
  </template>
  <Transition name="backdrop">
    <div
      v-if="route.name !== 'deck'"
      class="bg-neutral absolute top-0 left-0 z-2 h-full w-full cursor-pointer opacity-70 select-none hover:opacity-80 print:hidden"
      @click="router.push({ name: 'deck' })"
    />
  </Transition>
  <!-- Gardée en vie par KeepAlive, la vue suit encore la route : sans ce filtre elle ouvrirait aussi le document du chat (second lecteur vidéo invisible). -->
  <RouterView v-slot="{ Component, route }">
    <template v-if="Component && route.name === 'document'">
      <Transition
        :name="route.meta.transition"
        mode="in-out"
        appear
      >
        <component
          :is="Component"
          :key="route.path"
        ></component>
      </Transition>
    </template>
  </RouterView>
</template>
